import { ApprovalProcess, CostCenter, Employee, Expense, RightEnum, Role, StandardRoleEnum, SystemRightEnum } from "@finway-group/shared/lib/models"
import { PURCHASE_ORDER_STATUSES_PRE_APPROVAL } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import {
    checkIfUserIsPendingApproverOfExpense,
    getApprovalProcess,
    getApprovalProcessScopeFromExpenseStatus,
    getApproverIdsOfApprovalProcess,
} from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { requiredRightsAbsenceManagementSetting } from "@finway-group/shared/lib/utils/right.utils"
import { DeliveryNote } from "~Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { PurchaseOrder } from "~Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { AuthnService, UserService } from "~Shared/services"
import { store } from "~Shared/store"
import { RootState } from "~Shared/store/rootState.interface"
import { getNextApproverIds } from "~Shared/utils/approvalProcess.utils"
import { getCompanyProfile } from "../utils/auth.utils"
import { isRightGranted } from "../utils/helper.utils"

const SYSTEM_SETTINGS_REQUIRED_RIGHTS = [
    SystemRightEnum.SYS__COMPANY__ALL__READ,
    SystemRightEnum.SYS__USER__ALL__READ,
    SystemRightEnum.SYS__INTEGRATION__ALL__READ,
    SystemRightEnum.SYS__IDP__ALL__READ,
    SystemRightEnum.SYS__ROLE__ALL__READ,
    SystemRightEnum.SYS__PAYMENT_SETTINGS__ALL__MANAGE,
]

/**
 * Authorization Service - determines what an authenticated user can see and do
 */
export const AuthzService = {
    getUserRoleById: (id: string): Role | undefined => {
        const state: RootState = store.getState()
        const roles = state.roles.items
        return roles.find((role: Role) => role._id === id)
    },

    isRightGrantedForLoggedInUser: (right: RightEnum) => {
        const state: RootState = store.getState()
        return state.auth?.accessToken?.activeRights?.includes(right) || false
    },

    isAnyRightGrantedForLoggedInUser: (rights: Array<RightEnum>) => {
        const state: RootState = store.getState()
        return state.auth?.accessToken?.activeRights?.some((right) => rights.includes(right)) || false
    },

    isSystemRightGrantedForLoggedInUser: (right: SystemRightEnum) => {
        const state: RootState = store.getState()
        return state.auth?.accessToken?.systemRights?.includes(right) || false
    },

    canViewSystemSettings: () => {
        const state: RootState = store.getState()
        const systemRights = state.auth?.accessToken?.systemRights
        return systemRights && systemRights.some((right) => SYSTEM_SETTINGS_REQUIRED_RIGHTS.includes(right))
    },

    hasCompanySettingsUpdateSystemRight: () => AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__COMPANY__ALL__UPDATE),

    hasEmployeeSystemRight: (employee: Employee) => employee?.systemRights?.length === Object.values(SystemRightEnum).length || false,

    isDataUploaderGrantedForLoggedInUser: (): boolean =>
        AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__DATA_IMPORT__ALL__CREATE) && AuthzService.isUserDefaultAdmin(),

    isLoggedInUserPotentialApprover: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__BASIC__APPROVE),

    isLoggedInUserAllowedToApproveExpense: (expense: Expense): boolean => {
        const { accessToken } = AuthnService.getDataFromAccessToken()
        // if user is global approver
        if (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__APPROVE)) {
            return true
        }

        // if user is the next approver
        const isApproverOfApprovalProcess = checkIfUserIsPendingApproverOfExpense(expense.approvalProcesses, accessToken.id)
        if (isApproverOfApprovalProcess) {
            return true
        }

        // if user is approver of team and expense is from same team
        if (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__APPROVE) && getCompanyProfile(accessToken, expense.companyId)?.team === expense.team) {
            return true
        }
        // if user is substitute
        if (AuthzService.isLoggedInUserSubstitute(expense)) {
            return true
        }

        return false
    },

    isLoggedInUserGlobalApprover: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__APPROVE),

    isLoggedInUserAllowedToCreateExpenses: (): boolean => {
        const state: RootState = store.getState()
        return state.auth?.accessToken?.activeRights?.some((right) => [RightEnum.EXPENSE__ALL__CREATE, RightEnum.EXPENSE__ALL__CREATE_ON_BEHALF].includes(right)) || false
    },

    isLoggedInUserAllowedToCreatePurchaseOrders: (): boolean => {
        const state: RootState = store.getState()
        return state.auth?.accessToken?.activeRights?.some((right) => [RightEnum.EXPENSE__ALL__CREATE_ON_BEHALF].includes(right)) || false
    },

    isAllowedToCreateVendors: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__CREATE),

    isAllowedToCreateUnapprovedVendors: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__CREATE_UNAPPROVED),

    isPotentialApprover: (roleId: string) => {
        const role = AuthzService.getUserRoleById(roleId)
        return isRightGranted(role?.rights, RightEnum.EXPENSE__BASIC__APPROVE)
    },

    isGlobalApprover: (roleId: string) => {
        const role = AuthzService.getUserRoleById(roleId)
        return isRightGranted(role?.rights, RightEnum.EXPENSE__ALL__APPROVE)
    },

    canManageCardForUser: (cardHolder?: Employee) => {
        if (!cardHolder) {
            return false
        }
        if (!cardHolder.activeCompanyProfile?.weavrData?.userId) {
            return false
        } // discard user who is not enrolled in the card program

        const loggedInUser = UserService.getLoggedInEmployeeProfile()

        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD__ALL__WRITE) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD__TEAM__WRITE) && loggedInUser.activeCompanyProfile.team === cardHolder.activeCompanyProfile.team)
        )
    },

    isUserDefaultAdmin: (): boolean => {
        const role = UserService.getLoggedInEmployeeRole()
        return (role?.isReadOnly && role?.name === StandardRoleEnum.ADMIN) ?? false
    },

    canUpdateCompanyAccountingSettings: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.COMPANY_ACCOUNTING_SETTINGS__ALL__WRITE),
    canViewCompanyAccountingSettings: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.COMPANY_ACCOUNTING_SETTINGS__ALL__READ),
    canUpdateCompanyFeatureSettings: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.COMPANY_FEATURE_SETTINGS__ALL__WRITE),

    canUpdateFeatureCompanySettings: (): boolean =>
        AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__COMPANY__ALL__UPDATE) ||
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.COMPANY_FEATURE_SETTINGS__ALL__WRITE),

    canLoggedInUserArchiveRequest: (user: Employee, requestedBy: Employee): boolean =>
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ARCHIVE) ||
        (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__ARCHIVE) && user.activeCompanyProfile.team === requestedBy.activeCompanyProfile.team),

    canLoggedInUserDeleteRequest: (user: Employee, requestedBy: Employee): boolean =>
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__DELETE) ||
        (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__DELETE) && user.activeCompanyProfile.team === requestedBy.activeCompanyProfile.team),

    canLoggedInUserResetStatusRequest: (user: Employee, requestedBy: Employee): boolean =>
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__RESET_STATUS) ||
        (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__RESET_STATUS) && user.activeCompanyProfile.team === requestedBy.activeCompanyProfile.team),

    canUpdateUserApprovalData: (user: Employee): boolean => {
        const loggedInUser = UserService.getLoggedInEmployeeProfile()
        const isUserFromSameTeam = user.activeCompanyProfile?.team === loggedInUser.activeCompanyProfile.team
        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE_APPROVAL_DATA__ALL__UPDATE) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE_APPROVAL_DATA__TEAM__UPDATE) &&
                isUserFromSameTeam &&
                !isRightGranted(AuthzService.getUserRoleById(user.activeCompanyProfile.roleId)?.rights, RightEnum.EMPLOYEE_APPROVAL_DATA__ALL__UPDATE))
        )
    },
    canStartKyb: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD_WALLET__ALL__MANAGE),
    canManageTags: (editingTagsEnabled: boolean) =>
        // When editingTagsEnabled company setting is enabled then everyone can manage tags (except auditors), otherwise user has to have the right COMPANY_ACCOUNTING_SETTINGS__ALL__WRITE
        editingTagsEnabled ? AuthzService.isLoggedInUserAllowedToCreateExpenses() : AuthzService.canUpdateCompanyAccountingSettings(),

    canCreateSubscription: () =>
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.SUBSCRIPTION__ALL__WRITE) || AuthzService.isRightGrantedForLoggedInUser(RightEnum.SUBSCRIPTION__TEAM__WRITE),
    canUpdateSubscription: (isMyOwnSubscription: boolean, isSubscriptionOfMyTeam: boolean, isSubscriptionOfMyCostCenter: boolean) => {
        if (!AuthzService.isLoggedInUserAllowedToCreateExpenses()) {
            return false
        }

        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.SUBSCRIPTION__ALL__WRITE) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.SUBSCRIPTION__TEAM__WRITE) && isSubscriptionOfMyTeam) ||
            isMyOwnSubscription ||
            isSubscriptionOfMyCostCenter
        )
    },
    canLoggedInUserSeeBankAccounts: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.BANK_ACCOUNT__ALL__READ),
    canLoggedInUserExport: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__EXPORT),
    canLoggedInUserSeeExpensesToExport: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ACCESS_TABLE_EXPORT_READ),
    canLoggedInUserPayByBank: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__PAY_BY_BANK),
    canLoggedInUserSeeExpensesToPay: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ACCESS_TABLE_PAY_READ),
    canLoggedInUserReviewExpenses: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__REVIEW),
    canLoggedInUserSeeExpensesToReview: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ACCESS_TABLE_IN_REVIEW_READ),
    canLoggedInUserSeePurchseOrderProcurement: (): boolean =>
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__ALL__APPROVE) ||
        AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__PROCUREMENT__READ) ||
        AuthzService.isLoggedInUserGlobalApprover(),
    isUserPurchaseOrderApprover: (approverId: string): boolean => {
        const state: RootState = store.getState()
        const employees = state.employees.items
        const employee = employees.find((employee) => employee.id === approverId)

        if (!employee) {
            return false
        }
        // get approvers RolesActions
        const role = AuthzService.getUserRoleById(employee.activeCompanyProfile.roleId)

        return isRightGranted(role?.rights, RightEnum.PURCHASE_ORDER__ALL__APPROVE_REJECT_DASHBOARD)
    },
    isLoggedInUserSubstitute: (entity: { approvalProcesses: Array<ApprovalProcess<string>> }): boolean => {
        const { accessToken } = AuthnService.getDataFromAccessToken()
        return accessToken.substitutingFor?.some((id) => checkIfUserIsPendingApproverOfExpense(entity.approvalProcesses, id)) || false
    },
    isLoginUserSubstituteOnly: (expense: Expense<string>, costCenters: Array<CostCenter>): boolean => {
        const isUserFromSameTeam = UserService.isInMyTeam(expense.requestedBy._id)
        if (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ) && isUserFromSameTeam)
        ) {
            return false
        }

        if (AuthzService.isResponsibleForCostCenter(expense, costCenters)) {
            return false
        }

        return AuthzService.isLoggedInUserSubstitute(expense)
    },
    canApproverRejectFromDashboard: (expense: Expense<string>): boolean => {
        if (AuthzService.isLoggedInUserApprover(expense)) {
            return true
        }
        if (!AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__APPROVE_REJECT_DASHBOARD)) {
            return false
        }
        if (AuthzService.isLoggedInUserGlobalApprover()) {
            return true
        }
        const { accessToken } = AuthnService.getDataFromAccessToken()
        const approvalScope = getApprovalProcessScopeFromExpenseStatus(expense.status)
        const approvalProcess = approvalScope ? getApprovalProcess(approvalScope, expense.approvalProcesses) : undefined
        const isApproverOfApprovalProcess = getApproverIdsOfApprovalProcess(approvalProcess).includes(accessToken.id)
        if (isApproverOfApprovalProcess) {
            return true
        }
        if (AuthzService.isLoggedInUserSubstitute(expense)) {
            return true
        }
        return false
    },

    isResponsibleForCostCenter: (expense: Expense<string>, costCenters: Array<CostCenter>): boolean => {
        const { accessToken } = AuthnService.getDataFromAccessToken()
        const expenseSplitsCostCenters = costCenters.filter((cc) => expense.splits.some((split) => split.costCenter === cc._id))
        const parentCostCenters = costCenters.filter((cc) => expenseSplitsCostCenters.some((expenseSplitsCostCenter) => expenseSplitsCostCenter.parentCostCenter === cc._id))
        const expenseCostCenters = [...expenseSplitsCostCenters, ...parentCostCenters]
        const isResponsibleForCostCenter = expenseCostCenters?.some((cc) => cc.responsibleUser === accessToken.id) ?? false
        const hasReadAccessViaSharedCostCenters = costCenters?.some((cc) => cc.sharedUsers?.includes(accessToken.id)) ?? false
        return isResponsibleForCostCenter || hasReadAccessViaSharedCostCenters
    },

    canLoggedInUserAddReminders: (expense: Expense<string>, costCenters: Array<CostCenter>): boolean => {
        if (AuthzService.isLoginUserSubstituteOnly(expense, costCenters)) {
            return false
        }

        return true
    },
    canLoggedUserAttachToFolder: (expense: Expense<string>, costCenters: Array<CostCenter>): boolean => {
        if (AuthzService.isLoginUserSubstituteOnly(expense, costCenters)) {
            return false
        }

        return true
    },
    canLoggedInUserViewWallet: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD_WALLET__ALL__READ),
    canLoggedInUserViewTeamCards: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD__TEAM__READ),
    canLoggedInUserViewAllCards: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.CARD__ALL__READ),

    canViewExpenseFromCostCenterPage: (expense: Expense): boolean => {
        const isUserFromSameTeam = UserService.isInMyTeam(expense.requestedBy._id)

        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ) && isUserFromSameTeam)
        )
    },

    canViewEmployeeExpenses: (user: Employee): boolean => {
        const loggedInUser = UserService.getLoggedInEmployeeProfile()
        const isUserFromSameTeam = user.activeCompanyProfile?.team === loggedInUser.activeCompanyProfile.team
        return (
            loggedInUser.id === user.id ||
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ) && isUserFromSameTeam)
        )
    },
    canViewEmployeeDetails: (user: Employee): boolean => {
        const loggedInUser = UserService.getLoggedInEmployeeProfile()
        const isUserFromSameTeam = user.activeCompanyProfile?.team === loggedInUser.activeCompanyProfile.team
        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__TEAM__READ) && isUserFromSameTeam)
        )
    },
    canUpdateEmployeeDetails: (user?: Employee): boolean => {
        if (!user) {
            return false
        }
        const loggedInUser = UserService.getLoggedInEmployeeProfile()
        const isUserFromSameTeam = user.activeCompanyProfile?.team === loggedInUser.activeCompanyProfile.team
        return (
            AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__UPDATE) ||
            (AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__TEAM__UPDATE) && isUserFromSameTeam)
        )
    },
    canResetTfaForEmployee: (user: Employee): boolean => {
        const hasRightsToReset = AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__USER__ALL__UPDATE)
        const canReset = hasRightsToReset && user.settings?.security?.tfaEnabled!!

        return canReset
    },

    isAllowedToCreateExpenses: (roleId: string): boolean => {
        const role = AuthzService.getUserRoleById(roleId)
        return isRightGranted(role?.rights, RightEnum.EXPENSE__ALL__CREATE)
    },

    isAllowedToExportCompanyLevelChangeHistory: () => AuthzService.isRightGrantedForLoggedInUser(RightEnum.COMPANY_CHANGE_HISTORY__ALL__READ),
    isAllowedToExportRoleChangeHistory: () =>
        AuthzService.isAllowedToExportCompanyLevelChangeHistory() && AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__ROLE__ALL__READ),

    canSeeAbsenceManagementSettings: () => AuthzService.isAnyRightGrantedForLoggedInUser(requiredRightsAbsenceManagementSetting),

    isUserProcurementApprover: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__PROCUREMENT__APPROVE),

    isUserProcurementEditor: (): boolean => AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__PROCUREMENT__WRITE),

    isLoggedInUserRequestor: (purchaseOrder?: Pick<PurchaseOrder, "requestedBy">): boolean => purchaseOrder?.requestedBy?._id === UserService.getLoggedInEmployeeProfile().id,
    isLoggedInUserApprover: (request: PurchaseOrder | Expense): boolean => {
        const nextApproverIds = getNextApproverIds(request.approvalProcesses)
        return nextApproverIds?.includes(UserService.getLoggedInEmployeeProfile().id) ?? false
    },
    isLoggedInUserPurchaseOrderCreator: (purchaseOrder?: Pick<PurchaseOrder, "createdBy">): boolean =>
        purchaseOrder?.createdBy?._id === UserService.getLoggedInEmployeeProfile().id,
    isLoggedInUserAllowedToUpdatePurchaseOrder: (purchaseOrder: Pick<PurchaseOrder, "state" | "deleted" | "requestedBy" | "createdBy">): boolean => {
        const hasGeneralUpdateRights = AuthzService.isAnyRightGrantedForLoggedInUser([
            RightEnum.PURCHASE_ORDER__ALL__UPDATE,
            RightEnum.PURCHASE_ORDER__PROCUREMENT__WRITE,
            RightEnum.PURCHASE_ORDER__ALL__APPROVE,
        ])
        if (hasGeneralUpdateRights) {
            return true
        }

        const isRequester = AuthzService.isLoggedInUserRequestor(purchaseOrder)
        const isCreator = AuthzService.isLoggedInUserPurchaseOrderCreator(purchaseOrder)
        if ((isRequester || isCreator) && PURCHASE_ORDER_STATUSES_PRE_APPROVAL.includes(purchaseOrder.state.currentState)) {
            return true
        }

        return false
    },
    isLoggedInUserAllowedToDeletePurchaseOrder: (purchaseOrder?: Pick<PurchaseOrder, "requestedBy" | "createdBy">): boolean => {
        if (AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__ALL__DELETE)) {
            return true
        }

        const isRequester = AuthzService.isLoggedInUserRequestor(purchaseOrder)
        const isCreator = AuthzService.isLoggedInUserPurchaseOrderCreator(purchaseOrder)
        if (isRequester || isCreator) {
            return true
        }

        return false
    },
    canLoggedInUserApprovePurchaseOrder: (purchaseOrder: PurchaseOrder): boolean => {
        if (AuthzService.isLoggedInUserApprover(purchaseOrder)) {
            return true
        }
        if (!AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__ALL__APPROVE_REJECT_DASHBOARD)) {
            return false
        }
        if (AuthzService.isRightGrantedForLoggedInUser(RightEnum.PURCHASE_ORDER__ALL__APPROVE)) {
            return true
        }
        if (AuthzService.isLoggedInUserSubstitute(purchaseOrder)) {
            return true
        }
        return false
    },

    canLoggedInUserCompletePurchaseOrder: (purchaseOrder: PurchaseOrder): boolean => {
        // if user is creator
        if (AuthzService.isLoggedInUserPurchaseOrderCreator(purchaseOrder)) {
            return true
        }

        // if user is requester
        if (AuthzService.isLoggedInUserRequestor(purchaseOrder)) {
            return true
        }

        // if user is procurement approver
        if (AuthzService.isUserProcurementApprover()) {
            return true
        }

        // if user is procurement editor
        if (AuthzService.isUserProcurementEditor()) {
            return true
        }

        return false
    },

    isLoggedInUserCreator: (deliveryNote: Pick<DeliveryNote, "createdBy">): boolean => deliveryNote.createdBy._id === UserService.getLoggedInEmployeeProfile().id,
    hasUserUpdateRightsForDeliveryNote: (deliveryNote: Pick<DeliveryNote, "createdBy">): boolean =>
        AuthzService.isAnyRightGrantedForLoggedInUser([RightEnum.DELIVERY_NOTE__ALL__UPDATE]) || AuthzService.isLoggedInUserCreator(deliveryNote),
}
